import Vue from 'vue';
import { mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment-timezone';
import { guid } from '@/mixins/guid';
import { formatDate } from '@/mixins/format-date';

export default Vue.component('purchase-domain', {
  template: require('./template.html'),
  name: 'purchase-domain',
  mixins: [guid, formatDate],
  computed: {
    ...mapGetters(['priceSource', 'account', 'currentUser', 'identityId']),
  },
  props: ['modal', 'current-site', 'toggle-modal'],
  data() {
    return {
      loading: false,
      newdomain: '',
      purchased: false,
      purchaseErrors: [],
      searchResults: null,
      purchaseTerms: false,
      selectedTld: 'com',
      autoRenew: false,
      tlds: [],
      selectedPurchaseLength: 1,
      purchaseLengths: [],
      drzPrices: false,
    };
  },
  watch: {
    modal(o) {
      if (o) {
        this.resetPurchaseModal();
      }
    },
  },
  created() {
    // get drzzle fees from our central location
    this.drzPrices = this.priceSource;
    this.tlds = this.priceSource.tlds;
    this.purchaseLengths = this.priceSource.years;
  },
  methods: {
    async purchaseDomain() {
      if (this.purchaseTerms) {
        this.loading = true;
        try {
          const domain = await this._SEND([{
            name: 'purchaseNewDomain',
            payload: {
              currentUser: _.cloneDeep(this.currentUser),
              currentSite: {
                _id: this.currentSite._id,
                alias: this.currentSite.alias,
                name: this.currentSite.name,
                cloudFront: this.currentSite.cloudFront,
              },
              domain: {
                name: this.newdomain,
                tld: `.${this.selectedTld}`,
              },
              selectedPurchaseLength: this.selectedPurchaseLength,
              selectedTld: this.selectedTld,
              autoRenew: this.autoRenew,
              futureDates: this.getFutureDates(),
              currentTime: this.formatDate(Date.now()),
            },
          }]);

          this.$log.debug('Successfully purchased new domain! ', domain);

          // in state and db (for www CNAME), use the _id
          this.$store.dispatch('purchaseDomain', {
            site: this.currentSite,
            domain,
          });

          // save domain to this site's domains in db
          await this._SEND([{
            name: 'updateSiteAttr',
            payload: {
              siteId: this.currentSite._id,
              attr: 'domains',
              value: this.currentSite.domains,
            },
          }]);
          this.$log.debug('updateSiteAttr: domains');

          this.purchased = true;
          this.$emit('recheck-domains');
        } catch (e) {
          this.$log.error('error: ', e);
          // spit out error
          this.purchaseErrors.push(e.message || e);
          await this._reportError({
            error: e,
            fn: 'purchaseDomain',
          });
        } finally {
          this.loading = false;
          this._trackEvent({
            type: 'purchase_domain',
            meta: {
              site_name: this.currentSite.name,
              site_type: this.currentSite.type,
              site_tier: this.currentSite.tier,
              domain_name: this.newdomain,
              tld: this.selectedTld,
              purchase_length: this.selectedPurchaseLength,
              auto_renew: this.autoRenew,
            },
          });
        }
      }
    },
    getFutureDates() {
      // Here we construct the date to run our first auto renew check
      // by getting todays date - 45 days. We then set the year based
      // on how many years they purchased to domain for. All in UTC.
      let today = moment.utc(new Date());
      today = new Date(today);
      const renewYear = today.getFullYear() + parseInt(this.selectedPurchaseLength, 10);
      today = new Date(today.setFullYear(renewYear));
      const expireDate = new Date(today);
      const runOn = today.setDate(today.getDate() - 45);
      return {
        runOn: new Date(runOn).toISOString(),
        expireDate: new Date(expireDate).toISOString(),
        dateBought: new Date().toISOString(),
      };
    },
    getOneYearPrice() {
      const fee = this.drzPrices.fees.purchaseDomain;
      const tld = _.find(this.tlds, { tld: this.selectedTld });
      return parseInt(tld.price + fee, 10);
    },
    getPrice() {
      const fee = this.drzPrices.fees.purchaseDomain;
      const tld = _.find(this.tlds, { tld: this.selectedTld });
      const total = tld.price * this.selectedPurchaseLength;
      // aws price + convenience fee
      return total + fee;
    },
    closeModal(type) {
      this.$emit('toggle-modal', type, 'child');
    },
    searchDomain() {
      this.$validator.validateAll().then(async (result) => {
        if (result) {
          this.purchased = false;
          this.purchaseErrors = [];
          const domainName = `${this.newdomain}.${this.selectedTld}`;
          try {
            this.loading = true;

            // run the route53 check via lambda
            const check = await this._SEND([{
              name: 'searchDomainAvailability',
              payload: { domainName },
            }]);

            if (check.Availability === 'AVAILABLE') {
              this.searchResults = {
                domainName,
                available: true,
                awsError: false,
              };
            } else {
              this.searchResults = {
                domainName,
                available: false,
                awsError: false,
              };
            }
          } catch (e) {
            this.searchResults = {
              domainName: null,
              available: false,
              awsError: e.message,
            };
            this.$log.error('error: ', e);
            await this._reportError({
              error: e,
              fn: 'searchDomain',
            });
          } finally {
            this.loading = false;
          }
        }
      });
    },
    changeTld() {
      // if there is a domain name shown after a search result and a user then
      // tries to swap the tld in the select, we need to re-run the search.
      if (
        this.searchResults &&
        (this.searchResults.available || !this.searchResults.available) &&
        !this.loading &&
        !this.purchased
      ) {
        this.searchDomain();
      }
    },
    resetPurchaseModal() {
      this.purchased = false;
      this.purchaseErrors = [];
      this.searchResults = null;
      this.purchaseTerms = false;
      this.selectedTld = 'com';
      this.selectedPurchaseLength = 1;
      this.autoRenew = false;
    },
    formatErrors(ers) {
      return ers.join(' ');
    },
    nameCheck(e) {
      let val = e.currentTarget.value;
      val = val.replace(/[^a-zA-Z0-9]/g, '-');
      val = _.trim(val);
      this.newdomain = val;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/edit-site/domains/add-domain/purchase/index.js